import { Box, Group, Popover, Stack, Text, UnstyledButton } from '@mantine/core'
import { useCallback, useState, type ReactNode } from 'react'

import {
  getBoggleBadgeCategoryDisplay,
  getBoggleBadgeLevelDisplay,
  getBoggleBadgeMedallionColors,
} from '~/boggle/config/badges/categories'
import type { BoggleBadgeDefinition } from '~/boggle/config/badges/types'
import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'

export type BadgePopoverContentProps = {
  /** Per-rating earn counts for this badge. Only entries with `count > 0` render. */
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  definition: BoggleBadgeDefinition
  /** When true and `lockedReveal` is `mystery`, description shows `???`. */
  locked?: boolean
  /** Earn-count row (and “Not earned yet”) only in Compendium; hidden for run summary / reveals. */
  showEarnCounts?: boolean
}

/**
 * Inner content of the badge-detail Popover. Medallion shape + Compendium
 * grouping still communicate rarity at a glance; the popover repeats the level
 * name in WoW item-quality colors on the right of the earn-count row. v1 always shows the
 * description even when locked.
 */
export function BadgePopoverContent({
  countsByRating,
  definition,
  locked = false,
  showEarnCounts = false,
}: BadgePopoverContentProps) {
  const category = getBoggleBadgeCategoryDisplay(definition.category)
  const level = getBoggleBadgeLevelDisplay(definition.level)
  const { frame: categoryAccent } = getBoggleBadgeMedallionColors({
    badgeId: definition.id,
    category: definition.category,
  })
  const earnedCounts = BOGGLE_RUN_SUMMARY_RATINGS.flatMap(rating => {
    const count = countsByRating[rating.id] ?? 0
    if (count <= 0) {
      return []
    }
    return [{ count, ratingId: rating.id }]
  })

  return (
    <Stack gap={10} maw={260} data-testid={`badge-popover-${definition.id}`}>
      <Group align="flex-start" gap={10} justify="space-between" wrap="nowrap">
        <Text c="white" ff="Tilt Neon, sans-serif" fw={600} fz={21} lh={1.12}>
          {definition.name}
        </Text>
        {/* <Text
          c={categoryAccent}
          ff="Tilt Neon, sans-serif"
          fw={500}
          fz={14}
          lh={1.2}
          tt="uppercase"
          style={{ letterSpacing: 1 }}
        >
          {category.label}
        </Text> */}
      </Group>
      <Text c="rgba(232, 224, 210, 0.9)" fz={18} lh={1.4} style={{ textWrap: 'pretty' }}>
        {definition.description}
      </Text>
      <Group align="baseline" gap={24} justify="space-between" mt={showEarnCounts ? 4 : 0} wrap="nowrap">
        <Box style={{ flex: 1, minWidth: 0 }}>
          {showEarnCounts ? (
            earnedCounts.length > 0 ? (
              <Group gap={8} justify="flex-start" wrap="wrap">
                {earnedCounts.map(({ count, ratingId }) => (
                  <Group
                    data-testid={`badge-popover-rating-${definition.id}-${ratingId}`}
                    gap={5}
                    key={ratingId}
                    wrap="nowrap"
                  >
                    <RatingBadge h={24} ratingId={ratingId} />
                    <Text c="white" fw={600} fz={18} lh={1}>
                      {count}
                    </Text>
                  </Group>
                ))}
              </Group>
            ) : (
              <Text c="rgba(232, 224, 210, 0.5)" fz={18} lh={1.4}>
                Not earned yet
              </Text>
            )
          ) : null}
        </Box>
        <Text
          c={level.color}
          data-testid={`badge-popover-rarity-${definition.id}`}
          fz={13}
          lh={1}
          style={{ flexShrink: 0 }}
        >
          {level.label}
        </Text>
      </Group>
    </Stack>
  )
}

/**
 * Click-to-toggle Popover wrapper for badge details. The trigger is rendered
 * as `children` inside an `UnstyledButton` so it works on both desktop click
 * and mobile tap; pressing the same trigger twice closes the Popover.
 *
 * Used by the Compendium and the post-run BadgeList; one shared interaction
 * model for every place a badge appears. Pass `showEarnCounts` only from the
 * Compendium — run summary hides per-rating earn counts and “Not earned yet”.
 */
export function BadgePopover({
  children,
  countsByRating,
  definition,
  /** When true, the open trigger gets the same border + fill treatment as Compendium filter chips. */
  frameTriggerWhenOpen = false,
  locked = false,
  /** Controlled open state; when set, `onOpenChange` + `onTriggerClick` drive interaction. */
  opened: openedProp,
  onOpenChange,
  onOpened,
  onTriggerClick,
  showEarnCounts = false,
  p = 0,
}: {
  children: ReactNode
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  definition: BoggleBadgeDefinition
  frameTriggerWhenOpen?: boolean
  locked?: boolean
  opened?: boolean
  onOpenChange?: (opened: boolean) => void
  onOpened?: () => void
  onTriggerClick?: () => void
  showEarnCounts?: boolean
  p?: number
}) {
  const [openedInternal, setOpenedInternal] = useState(false)
  const isControlled = openedProp !== undefined
  const opened = isControlled ? openedProp : openedInternal

  const handleOpenChange = useCallback(
    (nextOpened: boolean) => {
      if (isControlled) {
        onOpenChange?.(nextOpened)
      } else {
        setOpenedInternal(nextOpened)
      }
      if (nextOpened) {
        onOpened?.()
      }
    },
    [isControlled, onOpenChange, onOpened],
  )
  const toggle = useCallback(() => {
    if (isControlled) {
      onTriggerClick?.()
    } else {
      handleOpenChange(!opened)
    }
  }, [handleOpenChange, isControlled, onTriggerClick, opened])

  const selected = frameTriggerWhenOpen && opened
  const frameComp = frameTriggerWhenOpen
  const { frameBorder } = getBoggleBadgeMedallionColors({
    badgeId: definition.id,
    category: definition.category,
  })

  return (
    <Popover
      onChange={handleOpenChange}
      opened={opened}
      position="top"
      shadow="md"
      styles={{ dropdown: { pointerEvents: 'none' } }}
      transitionProps={{ duration: 120 }}
      offset={6}
      withArrow={false}
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          aria-expanded={opened}
          aria-label={`Show details for ${definition.name}`}
          bd={
            frameComp
              ? `1px solid ${selected ? 'rgba(255,255,255,0.55)' : 'transparent'}`
              : undefined
          }
          bdrs={frameComp ? 12 : undefined}
          bg={selected ? 'rgba(255,255,255,0.14)' : 'transparent'}
          data-testid={`badge-popover-trigger-${definition.id}`}
          onClick={toggle}
          p={p}
          style={{
            alignItems: 'center',
            display: 'inline-flex',
            flexShrink: 0,
            justifyContent: 'center',
            pointerEvents: 'auto',
            transition: 'background-color 80ms ease, border-color 80ms ease',
          }}
        >
          {children}
        </UnstyledButton>
      </Popover.Target>
      {opened ? (
        <Popover.Dropdown
          bd={`2px solid ${frameBorder}`}
          bg="rgba(20, 18, 14, 0.96)"
          px={16}
          pt={14}
          pb={10}
        >
          <BadgePopoverContent
            countsByRating={countsByRating}
            definition={definition}
            locked={locked}
            showEarnCounts={showEarnCounts}
          />
        </Popover.Dropdown>
      ) : null}
    </Popover>
  )
}
